/**
 * Turning a stream of mediocre readings into one point worth publishing.
 *
 * ## The problem this solves
 *
 * A phone gives you a position about once a second, each one wrong by some
 * amount it estimates but cannot guarantee. Taking the newest one and calling
 * it a milestone's location is the obvious thing and it is not good enough:
 * the first reading after the screen wakes is routinely the worst of the day,
 * and one reading gives you no way at all to tell a good fix from a bad one.
 *
 * ## What is done instead
 *
 * Readings are kept in a rolling buffer and a point is the **weighted average
 * of the last few seconds**, each reading weighted by how tight it claimed to
 * be. That alone removes the cold-start reading. Then the app keeps improving
 * the same point for as long as you stand there.
 *
 * ## And the honest part
 *
 * Averaging does **not** divide the error by the square root of the count. GPS
 * errors over a few seconds are not independent: a signal bouncing off a wall
 * bounces off it for the whole minute. So the average is reported with the
 * **best margin any single reading claimed** — never better — and alongside it
 * the **spread**: how far apart the readings actually landed.
 *
 * The spread is what catches the lie. A phone saying "give or take five
 * metres" while its readings scatter over forty is a phone that is wrong, and
 * without the spread there is no way to know.
 */
import { fixQuality, reportedAccuracyM, type FixQuality } from "./fix.js";

/** One reading, as it came from the phone. */
export interface Sample {
  lat: number;
  lon: number;
  accuracyM: number;
  altitudeM: number | null;
  /** Milliseconds since the epoch. */
  at: number;
}

/** A place, worked out from several readings. */
export interface Spot {
  lat: number;
  lon: number;
  /** The best margin any of the used readings claimed, rounded up. */
  accuracyM: number;
  /** How far the furthest used reading landed from the average, rounded up. */
  spreadM: number;
  /** How many readings went into it. */
  samples: number;
  altitudeM: number | null;
  /** When the newest reading used was taken. */
  newestAt: number;
}

/** Readings older than this are not describing where you are standing now. */
const WINDOW_MS = 20_000;
/** More than this adds nothing and only slows the arithmetic. */
const MAX_SAMPLES = 40;
/** Metres per degree of latitude. Close enough anywhere on the Camino. */
const METRES_PER_DEGREE = 111_320;

/**
 * Distance between two nearby points, in metres.
 *
 * Flat-earth arithmetic on purpose: over the tens of metres this is used for,
 * it is off by well under a centimetre, and it is four lines instead of the
 * fifteen a great-circle formula needs.
 */
export function metresBetween(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const midLat = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  const dx = (b.lon - a.lon) * Math.cos(midLat) * METRES_PER_DEGREE;
  const dy = (b.lat - a.lat) * METRES_PER_DEGREE;
  return Math.hypot(dx, dy);
}

/**
 * The place those readings describe, or `null` if none of them are recent.
 *
 * Readings much looser than the best one are dropped rather than averaged in:
 * one reading claiming eighty metres would drag the average across the road,
 * and it carries no information the tight ones do not already have.
 *
 * That does mean that when conditions get worse — walking into a wood — the
 * tight readings from ten seconds ago win over the loose ones from now. It is
 * the right call, and it is bounded: those readings fall out of the window
 * within twenty seconds, and until they do, `newestAt` keeps saying how old
 * they are, so the freshness rule downgrades the spot on its own.
 */
export function averageSpot(samples: readonly Sample[], now: number): Spot | null {
  const fresh = samples.filter(
    (sample) =>
      Number.isFinite(sample.accuracyM) && sample.accuracyM > 0 && now - sample.at <= WINDOW_MS,
  );
  if (fresh.length === 0) return null;

  const best = Math.min(...fresh.map((sample) => sample.accuracyM));
  // The `+ 10` keeps a very good run from throwing away everything but one
  // reading: with a best of 3 m, a 12 m reading is still worth averaging in.
  const limit = Math.max(best * 3, best + 10);
  const kept = fresh.filter((sample) => sample.accuracyM <= limit).slice(-MAX_SAMPLES);

  let weight = 0;
  let lat = 0;
  let lon = 0;
  for (const sample of kept) {
    // Tighter readings count for more, and squared because the margin is a
    // radius: halving it makes a reading four times as informative.
    const w = 1 / (sample.accuracyM * sample.accuracyM);
    weight += w;
    lat += sample.lat * w;
    lon += sample.lon * w;
  }
  const middle = { lat: lat / weight, lon: lon / weight };

  let spread = 0;
  for (const sample of kept) spread = Math.max(spread, metresBetween(middle, sample));
  // Rounded up, like every other margin here — but not on floating-point dust.
  // Three identical readings are zero metres apart, and the arithmetic leaves
  // a remainder a billionth of a metre wide that a plain `ceil` would report
  // as a whole metre of disagreement. Exactly twelve metres came out as
  // thirteen for the same reason. The tolerance is a micrometre: far below
  // anything a phone could mean, and far above the noise.
  const spreadM = Math.max(0, Math.ceil(spread - 1e-6));

  const heights = kept
    .map((sample) => sample.altitudeM)
    .filter((height): height is number => height !== null && Number.isFinite(height));

  return {
    lat: middle.lat,
    lon: middle.lon,
    accuracyM: reportedAccuracyM(best),
    spreadM,
    samples: kept.length,
    altitudeM:
      heights.length === 0
        ? null
        : Math.round(heights.reduce((sum, h) => sum + h, 0) / heights.length),
    newestAt: Math.max(...kept.map((sample) => sample.at)),
  };
}

/**
 * Seconds since the newest reading the spot was built from, never negative.
 *
 * Phone clocks do drift, and a reading stamped in the future would otherwise
 * come out with a negative age and sail through every freshness check.
 */
export function spotAgeS(spot: Spot, now: number): number {
  return Math.max(0, (now - spot.newestAt) / 1000);
}

/** Spread up to here still describes an object you could touch. */
const GOOD_SPREAD_M = 8;
/** Beyond this the readings disagree about which side of the road you are on. */
const FAIR_SPREAD_M = 20;
/**
 * Fewer readings than this and the point cannot be called good, whatever the
 * numbers say.
 *
 * With one reading there is nothing to compare it against: a wild outlier and
 * a perfect fix look exactly the same. Two only tell you they agree or they
 * do not. Three is the first count where "they all landed together" means
 * something, and this app would rather say "regular" than flatter a guess.
 */
const MIN_SAMPLES_FOR_GOOD = 3;

const ORDER: Record<FixQuality, number> = { good: 0, fair: 1, bad: 2 };

function worse(a: FixQuality, b: FixQuality): FixQuality {
  return ORDER[a] >= ORDER[b] ? a : b;
}

/**
 * How much to trust a spot: the worst of what the phone claimed, how much the
 * readings disagreed, how old they are, and how many there were.
 */
export function spotQuality(spot: Spot, ageS: number): FixQuality {
  const bySpread: FixQuality =
    spot.spreadM <= GOOD_SPREAD_M ? "good" : spot.spreadM <= FAIR_SPREAD_M ? "fair" : "bad";
  const quality = worse(fixQuality(spot.accuracyM, ageS), bySpread);
  if (quality === "good" && spot.samples < MIN_SAMPLES_FOR_GOOD) return "fair";
  return quality;
}

/**
 * Whether a newly worked-out spot is worth replacing the stored one with.
 *
 * Only ever improves: standing still for twenty seconds must never turn a good
 * point into a worse one because the last two readings happened to wobble.
 */
export function isBetter(candidate: Spot, current: Spot): boolean {
  if (candidate.samples < current.samples) return false;
  if (candidate.accuracyM !== current.accuracyM) return candidate.accuracyM < current.accuracyM;
  return candidate.spreadM <= current.spreadM;
}
